import { Col, Row } from 'antd';
import { WithLogic } from 'App';
import { Input } from 'common/components/Form';
import { EUR, M3, T } from 'domain-constants';
import { TransportPlaceTask } from 'generated/backend-api';
import { ReadOnlyContactList } from 'generated/new-main';
import { observer } from 'mobx-react';
import numeral from 'numeral';
import qa from 'qa-selectors';
import { OptionData } from 'rc-select/lib/interface';
import { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { ellipse } from 'resources/images/common';
import { ItemAdd } from 'resources/images/planner';
import { ActivityGood, NEW_CLIENT_ITEM_VALUE } from '../ActivityCard';
import ActivityClient from '../ActivityClient';
import ActivityGoodsTable from '../ActivityGoodsTable';
import ActivityMoreDetails from '../ActivityMoreDetails';
import ActivityTaskBase, { ActivityBaseProps } from '../ActivityTaskBase';
import EditItem from '../EditItem';
import PlannerContentContainer from '../PlannerContentContainer';

interface Props extends ActivityBaseProps, WithLogic {
    goods: ActivityGood[];
    placeDisabled: boolean;
    refreshClient?: number;
    onDeleteClientClick?: (id: string, task: TransportPlaceTask) => void;
    onEditClientClick?: (id: string, task: TransportPlaceTask) => void;
    onNewClientClick?: (task: TransportPlaceTask) => void;
    onChangeUnloading?: () => void;
}

const ActivityTaskUnloading = (props: Props) => {
    const _getClient = (): string | undefined => {
        return props.task.contactId
            ? props.logic.plannerLogic().consigneeList.find(c => c.id === props.task.contactId)?.name ??
                  props.task.contactId
            : '';
    };

    const [selectedClient, setSelectedClient] = useState(_getClient());
    const [redraw, setRedraw] = useState(0);
    const [showMoreWeight, setShowMoreWeight] = useState(false);
    const [tWeight, setTWeight] = useState(props.logic.plannerLogic().getTaskWeight(props.task, T));
    const [mWeight, setMWeight] = useState(props.logic.plannerLogic().getTaskWeight(props.task, M3));
    const [selectedRows, setSelectedRows] = useState(props.task.unloadingGoods ?? []);

    useEffect(() => {
        setSelectedClient(_getClient());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshClient, props.task.contactId]);

    const { t } = props;

    const handleClientSelect = (value: string) => {
        if (value === NEW_CLIENT_ITEM_VALUE) {
            props.onNewClientClick?.(props.task);
        } else {
            const client = props.logic.plannerLogic().consigneeList.find(c => c.name === value);
            if (client) {
                props.task.contactId = client.id;
            }
            setSelectedClient(value);
        }
    };

    const handleClientChange = (value: string) => {
        if (value !== NEW_CLIENT_ITEM_VALUE) {
            const client = props.logic.plannerLogic().consigneeList.find(c => c.name === value);
            if (!client) {
                props.task.contactId = value;
                setSelectedClient(value);
            }
        }
    };

    const handleValueChange = (name: string, value: any) => {
        switch (name) {
            case 'shippmentPrice':
                if (props.task.shipmentPrice) {
                    props.task.shipmentPrice.price = value;
                } else {
                    props.task.shipmentPrice = {
                        price: value,
                        currency: EUR
                    };
                }
                break;
            case 'totalWeight':
                props.logic.plannerLogic().addOrRemoveMeasurement(props.task, value, T);
                props.logic.plannerLogic().addOrRemoveMeasurement(props.task, 0, M3);
                props.task.unloadingGoods = [];
                setSelectedRows(props.task.unloadingGoods);

                setTWeight(value);
                setMWeight(0);
                props.onChangeUnloading?.();
                break;
            case 'volume':
                props.logic.plannerLogic().addOrRemoveMeasurement(props.task, value, M3);
                setMWeight(value);
                break;
        }
    };

    const handleShowMoreClick = () => {
        setShowMoreWeight(!showMoreWeight);
        props.onExpadedWidth?.(props.index, !showMoreWeight);
    };

    const handleOnExpandClicked = (state: boolean) => {
        setRedraw(redraw + 1);
        if (!state) {
            setShowMoreWeight(false);
            props.onExpadedWidth?.(props.index, false);
        }
    };

    const _computeWeight = () => {
        let sum = 0;

        props.goods
            .filter(g => (props.task.unloadingGoods ? props.task.unloadingGoods.indexOf(g.id) > -1 : false))
            .forEach(good => {
                sum += good.measurements?.find(m => m.measurementUnit === M3)?.grossWeightVolume ?? 0;
            });
        props.logic.plannerLogic().addOrRemoveMeasurement(props.task, sum, M3);
        setMWeight(sum);

        sum = 0;
        props.goods
            .filter(g => (props.task.unloadingGoods ? props.task.unloadingGoods.indexOf(g.id) > -1 : false))
            .forEach(good => {
                sum +=
                    good.measurements?.find(m => m.measurementUnit === T)?.grossWeightVolume ??
                    good.grossWeightVolume ??
                    0;
            });

        props.logic.plannerLogic().addOrRemoveMeasurement(props.task, sum, T);
        setTWeight(sum);
    };

    const _getClientName = (client: ReadOnlyContactList | undefined): React.ReactNode | undefined => {
        if (client) {
            return (
                <EditItem
                    showDelete={false}
                    className="edit-active-client"
                    onButtonEditClick={() => {
                        props.onEditClientClick?.(String(client.id), props.task);
                    }}
                >
                    <Row className="title">{client.name}</Row>
                    {client.country?.iso2}, {client?.city}, {client?.street} {client?.houseNumber},{client?.postalCode}
                    <img src={ellipse} alt="ellipse-icon" />
                    {t('ClientContactListForm.businessId').toUpperCase()} {client.businessId}
                    <img src={ellipse} alt="ellipse-icon" />
                    {t('ClientContactListForm.vat').toUpperCase()} {client.vat}
                    <img src={ellipse} alt="ellipse-icon" />
                    {t('ClientContactListForm.taxId').toUpperCase()} {client.taxId}
                    <img src={ellipse} alt="ellipse-icon" />
                    {t('ClientContactListForm.otherIdentificationNumber').toUpperCase()}{' '}
                    {client.otherIdentificationNumber}
                </EditItem>
            );
        } else {
            return undefined;
        }
    };

    const newClient = {
        key: NEW_CLIENT_ITEM_VALUE,
        value: NEW_CLIENT_ITEM_VALUE,
        label: (
            <Row
                align="middle"
                className="add-new-activity-client"
                onClick={e => {
                    props.onNewClientClick?.(props.task);
                    e.stopPropagation();
                    e.preventDefault();
                }}
            >
                <img src={ItemAdd} alt="add-new-client" />
                {t('ContactList.createNew')}
            </Row>
        )
    };

    let clientOptions = props.logic.plannerLogic().consigneeList.map(
        (client, index) =>
            (({
                key: client.id,
                value: client.name,
                label: (
                    <EditItem
                        key={index}
                        onButtonDeleteClick={() => {
                            props.onDeleteClientClick?.(client.id!, props.task);
                        }}
                        onButtonEditClick={() => {
                            props.onEditClientClick?.(client.id!, props.task);
                        }}
                    >
                        {client.name}
                    </EditItem>
                )
            } as OptionData) ?? [])
    );
    clientOptions.unshift(newClient);

    const client = props.logic.plannerLogic().clientContactList.find(client => client.id === props.task.contactId);

    const measurements = props.task.measurements
        ?.sort((a, b) => {
            return a.measurementUnit === T || a.measurementUnit > b.measurementUnit ? -1 : 1;
        })
        .map(measurement => {
            return (
                <>
                    <img src={ellipse} alt="ellipse-icon" />
                    {numeral(measurement.grossWeightVolume ?? 0).format('0, 0.00')} {measurement.measurementUnit}
                </>
            );
        });

    const cmr = props.goods
        ?.filter(g => (props.task?.unloadingGoods ?? []).indexOf(g.id) > -1 && g.cmr)
        ?.map(g => g.cmr);

    return (
        <ActivityTaskBase
            {...props}
            className="activity-task-unloading"
            headerInfo={
                <Row className="activity-task-unloading-close-detail" align="middle">
                    {client ? client.name : selectedClient ?? ''} {measurements}{' '}
                    {cmr && cmr.length > 0 && (
                        <>
                            <img src={ellipse} alt="ellipse-icon" />
                            CMR {cmr.join(', ')}
                        </>
                    )}
                </Row>
            }
            onExpandedClick={handleOnExpandClicked}
        >
            <Row className="container">
                <Row>
                    <Col span={24}>
                        <Row>{t('PlannerPlacesTask.consignee')}</Row>
                        <ActivityClient
                            autoFocus={false}
                            disabled={props.placeDisabled}
                            refresh={props.refreshClient}
                            value={selectedClient}
                            qa={qa.planner.activity.inputCompanyName}
                            onChange={handleClientChange}
                            onSelect={handleClientSelect}
                            options={clientOptions}
                        />
                    </Col>
                </Row>
                {client && <Row className="activity-task-unloading-client">{_getClientName(client)}</Row>}
            </Row>
            {!showMoreWeight && (
                <>
                    <Row className="total-weight" align="middle">
                        <Col flex="auto">{t('PlannerPlacesTask.unloadedGoods')}</Col>
                        <Col className="total-weight-input">
                            <Row>{t('PlannerPlacesTask.totalWeight')}</Row>
                            <Input.Number
                                data-qa={qa.planner.activity.goods.fieldGrossWeightVolume}
                                disabled={props.placeDisabled || props.goods.length > 0}
                                disableStepIcon={true}
                                value={tWeight}
                                precision={1}
                                min={0}
                                postfix={`[${T}]`}
                                onChange={value => {
                                    handleValueChange('totalWeight', value);
                                }}
                            />
                        </Col>
                        <Col className="total-volume-input">
                            <Row>{t('PlannerPlacesTask.volume')}</Row>
                            <Input.Number
                                data-qa={qa.planner.activity.goods.fieldVolume}
                                disabled={props.placeDisabled || props.goods.length > 0}
                                disableStepIcon={true}
                                value={mWeight}
                                precision={1}
                                min={0}
                                postfix={`[${M3}]`}
                                onChange={value => {
                                    handleValueChange('volume', value);
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className="weight-show-more" justify="end">
                        <Col onClick={handleShowMoreClick}>{t('PlannerPlacesTask.moreDetails')}</Col>
                    </Row>
                </>
            )}
            {showMoreWeight && (
                <PlannerContentContainer
                    className="add-goods"
                    title={t('PlannerPlacesTask.unloadedGoods')}
                    expanded={true}
                    expandable={false}
                >
                    <Row>
                        <Row gutter={12} align="middle">
                            <Col span={5}>
                                <Row>{t('PlannerPlacesTask.totalWeight')}</Row>
                                <Input.Number
                                    data-qa={qa.planner.activity.goods.fieldGrossWeightVolume}
                                    disabled={true}
                                    value={tWeight}
                                    precision={1}
                                    min={0}
                                    postfix={`[${T}]`}
                                    onChange={value => {
                                        handleValueChange('totalWeight', value);
                                    }}
                                />
                            </Col>
                            <Col span={5}>
                                <Row>{t('PlannerPlacesTask.volume')}</Row>
                                <Input.Number
                                    data-qa={qa.planner.activity.goods.fieldVolume}
                                    disabled={true}
                                    value={mWeight}
                                    precision={1}
                                    min={0}
                                    postfix={`[${M3}]`}
                                    onChange={value => {
                                        handleValueChange('volume', value);
                                    }}
                                />
                            </Col>
                            <Col span={14} />
                        </Row>
                    </Row>
                    <Row className="goods-table">
                        <ActivityGoodsTable
                            logic={props.logic}
                            editable={false}
                            disabled={props.placeDisabled}
                            rowSelection={{
                                selectedRowKeys: selectedRows,
                                onChange: (selectedRowKeys: React.Key[]) => {
                                    props.task.unloadingGoods = selectedRowKeys.map(k => String(k)) ?? [];
                                    _computeWeight();
                                    setSelectedRows(props.task.unloadingGoods);
                                    props.onChangeUnloading?.();
                                },
                                getCheckboxProps: (record: ActivityGood) => {
                                    return {
                                        disabled: (record as ActivityGood).disabled || props.placeDisabled,
                                        name: record.id
                                    };
                                }
                            }}
                            goods={props.goods.map(g => {
                                const disabled =
                                    g.disabled && (props.task.unloadingGoods ?? []).indexOf(g.id) === -1 ? true : false;
                                return {
                                    ...g,
                                    disabled: disabled
                                };
                            })}
                            placeDisabled={props.placeDisabled}
                        />
                    </Row>
                    <Row className="weight-show-more" justify="end">
                        <Col onClick={handleShowMoreClick}>{t('PlannerPlacesTask.lessDetails')}</Col>
                    </Row>
                </PlannerContentContainer>
            )}
            <ActivityMoreDetails task={props.task} />
        </ActivityTaskBase>
    );
};

export default withTranslation()(observer(ActivityTaskUnloading));
